import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'wouter';
import {
  trackPageView,
  trackEvent,
  trackViewItem,
  trackAddToCart,
  trackBeginCheckout,
  trackCheckoutStep,
  trackAddShippingInfo,
  trackAddPaymentInfo,
  trackPurchase,
  trackNewsletterSignup,
  trackWaitlistSignup,
  trackShare,
  trackLogin,
  trackSignUp,
  setPlantCategoryDimension,
  setUserProperties,
  setUserId,
  clearUserId,
  initScrollDepthTracking,
  initEngagementTimeTracking,
  buildGA4Item,
  calculateItemsValue,
  type GA4UserProperties,
} from '@/lib/analytics';

/**
 * Hook for automatic page view tracking
 * Tracks page views on route changes
 */
export function usePageTracking() {
  const [location] = useLocation();
  const previousLocation = useRef<string>('');

  useEffect(() => {
    // Only track if location actually changed
    if (location !== previousLocation.current) {
      previousLocation.current = location;

      // Map routes to page titles
      const pageTitles: Record<string, string> = {
        '/': 'Home',
        '/checkout': 'Checkout',
        '/checkout/success': 'Order Confirmation',
        '/waitlist': 'Waitlist',
        '/waitlist/success': 'Waitlist Confirmation',
        '/portal': 'Customer Portal',
        '/admin': 'Admin Dashboard',
        '/workshop': 'Workshop Booking',
        '/workshop/success': 'Workshop Confirmation',
      };

      const pageTitle = pageTitles[location] || 'Life Itself Studio';
      trackPageView(location, `${pageTitle} | Life Itself Studio`);
    }
  }, [location]);
}

/**
 * Hook for scroll depth and engagement time tracking
 * Initializes tracking on mount, cleans up on unmount
 */
export function useEngagementTracking() {
  useEffect(() => {
    const cleanupScroll = initScrollDepthTracking();
    const cleanupEngagement = initEngagementTimeTracking();

    return () => {
      cleanupScroll();
      cleanupEngagement();
    };
  }, []);
}

/**
 * Hook for checkout flow tracking
 * Provides methods to track each step of the checkout process
 */
export function useCheckoutAnalytics() {
  const trackBiomeSelection = useCallback((biome: string) => {
    trackViewItem(
      `terrarium-${biome}`,
      `${biome.charAt(0).toUpperCase() + biome.slice(1)} Terrarium`,
      'Terrariums',
      499,
      biome
    );
    setPlantCategoryDimension(biome);
    trackEvent('select_biome', { biome });
  }, []);

  const trackUpgradeToggle = useCallback((
    upgradeType: 'premium_plants' | 'custom_design' | 'experience',
    enabled: boolean,
    experienceType?: '2-person' | '4-person'
  ) => {
    const upgradePrices = {
      premium_plants: 499,
      custom_design: 999,
      experience: experienceType === '2-person' ? 1501 : 3501,
    };

    if (enabled) {
      trackAddToCart(
        `upgrade-${upgradeType}`,
        upgradeType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
        'Upgrades',
        upgradePrices[upgradeType]
      );
    } else {
      trackEvent('remove_upgrade', {
        upgrade_type: upgradeType,
        value: upgradePrices[upgradeType],
      });
    }
  }, []);

  const trackDeliverySelection = useCallback((deliveryWeek: string) => {
    trackEvent('select_delivery_week', {
      delivery_week: deliveryWeek,
    });
  }, []);

  const trackInsuranceSelection = useCallback((
    plan: 'monthly' | 'annual' | 'none'
  ) => {
    trackEvent('select_insurance', {
      insurance_plan: plan,
      has_insurance: plan !== 'none',
    });
  }, []);

  const trackGiftToggle = useCallback((isGift: boolean) => {
    trackEvent('toggle_gift_option', { is_gift: isGift });
  }, []);

  const trackStepProgress = useCallback((
    stepNumber: number,
    stepName: string,
    checkoutState: {
      biome: string;
      hasPremiumPlants: boolean;
      hasCustomDesign: boolean;
      experienceType: 'none' | '2-person' | '4-person';
    }
  ) => {
    const items = buildGA4Item(
      checkoutState.biome,
      checkoutState.hasPremiumPlants,
      checkoutState.hasCustomDesign,
      checkoutState.experienceType
    );
    const value = calculateItemsValue(items);

    trackCheckoutStep(stepNumber, stepName, items, value);

    // Track specific checkout events based on step
    switch (stepNumber) {
      case 1:
        // Beginning checkout
        trackBeginCheckout(items, value);
        break;
      case 4:
        // Location/shipping info added
        trackAddShippingInfo(items, value, 'local_delivery');
        break;
      case 9:
        // Payment step
        trackAddPaymentInfo(items, value, 'stripe');
        break;
    }
  }, []);

  const trackCheckoutComplete = useCallback((
    transactionId: string,
    checkoutState: {
      biome: string;
      hasPremiumPlants: boolean;
      hasCustomDesign: boolean;
      experienceType: 'none' | '2-person' | '4-person';
      isGift: boolean;
      wantsInsurance: boolean;
    }
  ) => {
    const items = buildGA4Item(
      checkoutState.biome,
      checkoutState.hasPremiumPlants,
      checkoutState.hasCustomDesign,
      checkoutState.experienceType
    );
    const value = calculateItemsValue(items);

    trackPurchase(transactionId, value, items, {
      affiliation: 'Life Itself Studio',
    });

    // Track additional purchase context
    trackEvent('purchase_details', {
      is_gift: checkoutState.isGift,
      has_insurance: checkoutState.wantsInsurance,
      biome: checkoutState.biome,
      has_premium_plants: checkoutState.hasPremiumPlants,
      has_custom_design: checkoutState.hasCustomDesign,
      experience_type: checkoutState.experienceType,
    });
  }, []);

  return {
    trackBiomeSelection,
    trackUpgradeToggle,
    trackDeliverySelection,
    trackInsuranceSelection,
    trackGiftToggle,
    trackStepProgress,
    trackCheckoutComplete,
  };
}

/**
 * Hook for product/gallery tracking
 * Provides methods to track product views and interactions
 */
export function useProductAnalytics() {
  const trackProductView = useCallback((
    productId: string,
    productName: string,
    category: string,
    price: number
  ) => {
    trackViewItem(productId, productName, category, price);
  }, []);

  const trackProductClick = useCallback((
    productId: string,
    productName: string,
    position?: number
  ) => {
    trackEvent('select_item', {
      item_id: productId,
      item_name: productName,
      item_list_name: 'Gallery',
      index: position,
    });
  }, []);

  const trackGalleryView = useCallback((
    items: Array<{ id: string; name: string; category: string; price: number }>
  ) => {
    trackEvent('view_item_list', {
      item_list_id: 'gallery',
      item_list_name: 'Terrarium Gallery',
      items: items.map((item, index) => ({
        item_id: item.id,
        item_name: item.name,
        item_category: item.category,
        price: item.price,
        index,
      })),
    });
  }, []);

  return {
    trackProductView,
    trackProductClick,
    trackGalleryView,
  };
}

/**
 * Hook for lead generation tracking
 * Tracks newsletter signups, waitlist joins, and contact form submissions
 */
export function useLeadAnalytics() {
  const trackNewsletter = useCallback((source?: string) => {
    trackNewsletterSignup(source);
  }, []);

  const trackWaitlist = useCallback((state: string, biomePreference?: string) => {
    trackWaitlistSignup(state, biomePreference);
  }, []);

  const trackContact = useCallback((formType: string) => {
    trackEvent('contact_form_submit', { form_type: formType });
  }, []);

  return {
    trackNewsletter,
    trackWaitlist,
    trackContact,
  };
}

/**
 * Hook for social sharing tracking
 */
export function useSocialAnalytics() {
  const trackSocialShare = useCallback((
    platform: 'twitter' | 'facebook' | 'copy_link' | 'email',
    contentType: string,
    itemId?: string
  ) => {
    trackShare(platform, contentType, itemId);
  }, []);

  return { trackSocialShare };
}

/**
 * Hook for user authentication tracking
 */
export function useAuthAnalytics() {
  const trackUserLogin = useCallback((method: string, userId?: string) => {
    trackLogin(method);
    if (userId) {
      setUserId(userId);
    }
  }, []);

  const trackUserSignup = useCallback((method: string, userId?: string) => {
    trackSignUp(method);
    if (userId) {
      setUserId(userId);
    }
  }, []);

  const trackUserLogout = useCallback(() => {
    clearUserId();
    trackEvent('logout');
  }, []);

  const updateUserProperties = useCallback((properties: GA4UserProperties) => {
    setUserProperties(properties);
  }, []);

  return {
    trackUserLogin,
    trackUserSignup,
    trackUserLogout,
    updateUserProperties,
  };
}

/**
 * Hook for CTA and interaction tracking
 */
export function useInteractionAnalytics() {
  const trackCTAClick = useCallback((
    ctaName: string,
    ctaLocation: string,
    destination?: string
  ) => {
    trackEvent('cta_click', {
      cta_name: ctaName,
      cta_location: ctaLocation,
      destination,
    });
  }, []);

  const trackButtonClick = useCallback((
    buttonName: string,
    buttonLocation: string
  ) => {
    trackEvent('button_click', {
      button_name: buttonName,
      button_location: buttonLocation,
    });
  }, []);

  const trackAccordionToggle = useCallback((
    accordionId: string,
    isOpen: boolean
  ) => {
    trackEvent('accordion_toggle', {
      accordion_id: accordionId,
      is_open: isOpen,
    });
  }, []);

  const trackTabChange = useCallback((tabId: string, tabName: string) => {
    trackEvent('tab_change', {
      tab_id: tabId,
      tab_name: tabName,
    });
  }, []);

  return {
    trackCTAClick,
    trackButtonClick,
    trackAccordionToggle,
    trackTabChange,
  };
}

// Re-export types
export type { GA4UserProperties };
